// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ListingCategories from "@gcg/App/Components/ListingCategories/ListingCategories";
import loadSubBrands from "@gcg/App/Handlers/LoadSubBrands";
import { SubBrandState } from "@gcg/App/Store/Reducers/SubBrandsReducer";
import { ProductListPageContext } from "@gcg/Overrides/Pages/ProductListPage";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedBrandPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { BrandStateContext, getBrandStateByPath } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";

import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BrandDetailsPageContext } from "@insite/content-library/Pages/BrandDetailsPage";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import React, { FC, useContext, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    uppercaseTitle = "uppercaseTitle",
    boldTitle = "boldTitle",
    addSubBrandsNumberToTitle = "addSubBrandsNumberToTitle",
    addBrandNameToTitle = "addBrandNameToTitle",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.uppercaseTitle]: boolean;
        [fields.boldTitle]: boolean;
        [fields.addSubBrandsNumberToTitle]: boolean;
        [fields.addBrandNameToTitle]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState & SubBrandState) => {
    const isLoading = state.subBrands.isLoading;
    const brands = state.subBrands.brands;
    const { pathname } = getLocation(state);
    const brandPath = getSelectedBrandPath(state) || pathname;
    const brandState = getBrandStateByPath(state, brandPath).value;

    return {
        isLoading,
        brands,
        brandState,
    };
};

const mapDispatchToProps = {
    loadSubBrands,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ShopBySubBrandStyles {
    title?: TypographyProps;
    categoryItemRow?: GridItemProps;
    innerCategoryContainer?: GridContainerProps;
    categoryImageItem?: GridItemProps;
    categoryImageLink?: LinkPresentationProps;
}

export const shopBySubBrandStyles: ShopBySubBrandStyles = {
    title: {
        variant: "h3",
    },
    categoryItemRow: {
        width: [6, 6, 4, 3, 2],
    },
    categoryImageItem: {
        width: 12,
        align: "middle",
        css: css`
            width: 100%;
            justify-content: center;
            margin-bottom: 5px !important;
        `,
    },
};

const styles = shopBySubBrandStyles;

const ShopBySubBrand: FC<Props> = ({ fields, isLoading, brandState, brands, loadSubBrands }) => {
    const { title } = fields;

    useEffect(() => {
        if (brandState && brandState.id) {
            const brandId = brandState.id;
            loadSubBrands({ brandId } as any);
        }
    }, [brandState]);

    if (!brands || !brands.length) {
        return <></>;
    }

    return (
        <StyledWrapper data-test-selector="brandCategories">
            <Typography
                {...styles.title}
                css={css`
                    font-weight: ${fields.boldTitle ? "600" : "normal"};
                    text-transform: ${fields.uppercaseTitle ? "uppercase" : "initial"};
                    letter-spacing: 0.3px;
                `}
                data-test-selector="brandCategoriesTitle"
            >
                {fields.addSubBrandsNumberToTitle && [translate("Found"), brands.length, ""].join(" ")}
                {title}
                {fields.addBrandNameToTitle && ["", brandState?.name || (brandState as any)?.brandName].join(" ")}
            </Typography>
            <ListingCategories items={brands} brand={brandState} showImages={true} />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ShopBySubBrand),
    definition: {
        group: "Brand Details",
        displayName: "Shop By Sub Brands",
        allowedContexts: [BrandDetailsPageContext, ProductListPageContext],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Shop By Sub Brands",
                fieldType: "Translatable",
            },
            {
                name: fields.uppercaseTitle,
                displayName: "Uppercase Title",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.boldTitle,
                displayName: "Bold Title",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.addSubBrandsNumberToTitle,
                displayName: "Add Sub Brands Number to Title",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.addBrandNameToTitle,
                displayName: "Add Brand Name to Title",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
